import { useContext, useEffect, useState, useRef } from 'react';
import {
    UNSAFE_LocationContext as RouterLocationContext,
    UNSAFE_RouteContext as RouterRouteContext,
    useLocation,
    useNavigate,
    useOutlet,
    useParams,
} from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
// @mui
import { Box } from '@mui/material';
// hooks

// components


import Main from './Main';
import MainTabs from './MainTabs';
import { getModuleBasePath, ModuleNavigationProvider } from './ModuleNavigation';
import Header from './header';

import NavVertical from './nav/NavVertical';

import { NAV } from '../../config-global';
import Footer from './Footer';
import DataImportService from '../../services/dataimport.service';
import LoginDeailsService from '../../services/logindetails.service';
import { AppStoreProvider } from '../../store/appStore';

// ----------------------------------------------------------------------

const EMPTY_TABS_ROUTE = '/masters';
const isTablessRoute = (route = '') => route.replace(/\/+$/, '') === EMPTY_TABS_ROUTE;

function FrozenRouterContext({ children, locationContext, routeContext }) {
    return (
        <RouterLocationContext.Provider value={locationContext}>
            <RouterRouteContext.Provider value={routeContext}>
                {children}
            </RouterRouteContext.Provider>
        </RouterLocationContext.Provider>
    );
}

FrozenRouterContext.propTypes = {
    children: PropTypes.node,
    locationContext: PropTypes.object,
    routeContext: PropTypes.object,
};

export default function MainLayout() {

    const location = useLocation();
    const navigate = useNavigate();
    const outlet = useOutlet();
    const routeContext = useContext(RouterRouteContext);
    const locationContext = useContext(RouterLocationContext);

    const { masterName } = useParams();
    function formatPageName(pageName) {
        const cleanedPageName = pageName ? pageName.trim().replace(/-/g, ' ') : '';
        const formattedPageName = cleanedPageName.replace(/\b\w/g, char => char.toUpperCase());
        return formattedPageName;
    }
    const headerName = formatPageName(masterName);

    const [displayTabCount, setDisplayTabCount] = useState(() => {
        const stored = sessionStorage.getItem('displayTabCount');
        return stored !== null ? Number(stored) : 0;
    });
    const isMultiTabEnabled = displayTabCount > 0;

    const [open, setOpen] = useState(true);
    const [navWidth, setNavWidth] = useState(NAV.W_DASHBOARD);
    const [openTabs, setOpenTabs] = useState(() => {
        const stored = sessionStorage.getItem('displayTabCount');
        // Only if displayTabCount was explicitly stored and is <= 0 do we treat multi-tab as disabled
        if (stored !== null && Number(stored) <= 0) {
            sessionStorage.setItem('mainOpenTabs', JSON.stringify([]));
            return [];
        }
        try {
            const savedTabs = JSON.parse(sessionStorage.getItem('mainOpenTabs')) || [];
            const tabs = savedTabs.filter((tab) => !isTablessRoute(tab.route));
            sessionStorage.setItem('mainOpenTabs', JSON.stringify(tabs));
            return tabs;
        } catch (error) {
            return [];
        }
    });
    const [cachedTabOutlets, setCachedTabOutlets] = useState({});
    const [cachedTabPaths, setCachedTabPaths] = useState({});
    const [tabRefreshKeys, setTabRefreshKeys] = useState({});
    const [singleTabRefreshKey, setSingleTabRefreshKey] = useState(0);
    // ─── Auto-logout when IsRunningDataImporting = true (only on navigation) ────
    const isFirstRender = useRef(true);

    useEffect(() => {
        // Skip the initial mount — only check on subsequent navigations
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        const checkDataImportStatus = async () => {
            try {
                const service = await DataImportService;
                const response = await service.GetIsRunningDataImporting();
                const importing = response?.data?.isRunningDataImporting === true ||
                    response?.data?.IsRunningDataImporting === true;

                if (importing) {
                    // Clear session and redirect to login
                    localStorage.clear();
                    sessionStorage.clear();
                    window.location.href = '/login';
                }
            } catch (error) {
                // Handle 409 Conflict as "data importing is active" — force logout
                if (error?.response?.status === 409) {
                    localStorage.clear();
                    sessionStorage.clear();
                    window.location.href = '/login';
                } else {
                    // Silently ignore — don't disrupt the user if the API is temporarily unreachable
                    console.error('Error checking data import status:', error);
                }
            }
        };

        checkDataImportStatus();
    }, [location.pathname]);

    useEffect(() => {
        LoginDeailsService.CallLoginAPI()
            .then((response) => {
                const rawCount = response?.data?.displayTabCount ?? response?.data?.DisplayTabCount;
                if (response.status === 200 && rawCount !== undefined && rawCount !== null) {
                    const count = Number(rawCount);
                    sessionStorage.setItem('displayTabCount', count);
                    setDisplayTabCount(count);
                    if (count <= 0) {
                        setOpenTabs([]);
                        setCachedTabOutlets({});
                        setCachedTabPaths({});
                        sessionStorage.setItem('mainOpenTabs', JSON.stringify([]));
                    } else {
                        try {
                            const savedTabs = JSON.parse(sessionStorage.getItem('mainOpenTabs')) || [];
                            const tabs = savedTabs.filter((tab) => !isTablessRoute(tab.route)).slice(0, count);
                            if (tabs.length > 0) {
                                setOpenTabs(tabs);
                                sessionStorage.setItem('mainOpenTabs', JSON.stringify(tabs));
                            }
                        } catch (e) {
                            // ignore
                        }
                    }
                }
            })
            .catch((err) => {
                console.error('Error fetching login details in MainLayout:', err);
            });
    }, []);

    // Clear all tabs when multi-tab is explicitly disabled
    useEffect(() => {
        const stored = sessionStorage.getItem('displayTabCount');
        if (stored !== null && Number(stored) <= 0) {
            setOpenTabs([]);
            setCachedTabOutlets({});
            setCachedTabPaths({});
            sessionStorage.setItem('mainOpenTabs', JSON.stringify([]));
        }
    }, [displayTabCount]);





    const handleOpen = () => {
        setOpen(true);
        setNavWidth(NAV.W_DASHBOARD);
    };

    const handleClose = () => {
        setOpen(false);
        setNavWidth(0);
    };

    useEffect(() => {
        const handleHide = () => handleClose();
        window.addEventListener('hideSidebarEvent', handleHide);
        return () => window.removeEventListener('hideSidebarEvent', handleHide);
    }, []);

    useEffect(() => { // NOSONAR
        const filteredUrl = JSON.parse(sessionStorage.getItem('filteredUrl'));
        const filterPayload = JSON.parse(sessionStorage.getItem('filterPayload'));
        const gridPageNumber = JSON.parse(sessionStorage.getItem('gridPageNumber'));
        const searchClaimData = JSON.parse(sessionStorage.getItem('searchClaimData'));

        const CAHDetails = JSON.parse(sessionStorage.getItem("CAHDetails"));
        const ClaimSubmissionDetails = JSON.parse(sessionStorage.getItem("ClaimSubmissionDetails"));
        const ClaimClosureHistoryReport = JSON.parse(sessionStorage.getItem("ClaimClosureHistoryReportFilterDetails"));
        const SchemeDefinitionReportFilterState = JSON.parse(sessionStorage.getItem("SchemeDefinitionReportFilterState"));

        const claimSummaryReportFilterState = JSON.parse(sessionStorage.getItem("ClaimSummaryReportFilterState"))

        if (claimSummaryReportFilterState && !location.pathname.includes(claimSummaryReportFilterState?.currentUrl)) {
            sessionStorage.removeItem('ClaimSummaryReportFilterState');
        }

        if (CAHDetails && !location.pathname.includes(CAHDetails.currentUrl)) {
            sessionStorage.removeItem('CAHDetails');
        }

        if (ClaimSubmissionDetails && !location.pathname.includes(ClaimSubmissionDetails.currentUrl)) {
            sessionStorage.removeItem('ClaimSubmissionDetails');
        }

        if (SchemeDefinitionReportFilterState && !location.pathname.includes(SchemeDefinitionReportFilterState.currentUrl)) {
            sessionStorage.removeItem('SchemeDefinitionReportFilterState');
        }

        if (ClaimClosureHistoryReport && !location.pathname.includes(ClaimClosureHistoryReport.currentUrl)) {
            sessionStorage.removeItem('ClaimClosureHistoryReportFilterDetails');
        }

        if (filteredUrl && !location.pathname.includes(filteredUrl.currentUrl)) {
            sessionStorage.removeItem('filteredUrl');
        }

        if (filterPayload && !location.pathname.includes(filterPayload.currentUrl)) {
            sessionStorage.removeItem('filterPayload');
            sessionStorage.removeItem('filterValues');
        }

        if (!filterPayload || !location.pathname.includes(filterPayload.currentUrl)) {
            sessionStorage.removeItem('filterValues');
        }

        if (gridPageNumber && !location.pathname.includes(gridPageNumber.currentUrl)) {
            sessionStorage.removeItem('gridPageNumber');
        }

        if (searchClaimData && !location.pathname.includes(searchClaimData.CurrentUrl)) {
            sessionStorage.removeItem('searchClaimData');
        }



        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const formatRouteLabel = (pathname) => {
        const pathParts = pathname.split('/').filter(Boolean);
        const lastPathPart = pathParts[pathParts.length - 1] || 'Home';
        return formatPageName(lastPathPart);
    };

    const normalizeRoute = (route) => {
        if (!route) {
            return '/';
        }

        return route.startsWith('/') ? route : `/${route}`;
    };

    const getBaseRoute = (pathname) => {
        const moduleBasePath = getModuleBasePath(pathname);
        if (moduleBasePath) return moduleBasePath;
        const pathParts = pathname.split('/').filter(Boolean);
        const detailSegmentIndex = pathParts.findIndex((pathPart, index) => (
            index > 1 && ['add', 'edit', 'filter'].includes(pathPart)
        ));

        if (detailSegmentIndex > -1) {
            return `/${pathParts.slice(0, detailSegmentIndex).join('/')}`;
        }

        return pathname;
    };

    const upsertTab = (nextTab) => {
        if (!isMultiTabEnabled) {
            return;
        }
        const normalizedRoute = normalizeRoute(nextTab.route);
        const route = getModuleBasePath(normalizedRoute) || normalizedRoute;
        if (isTablessRoute(route)) {
            return;
        }

        setOpenTabs((previousTabs) => {
            const existingTab = previousTabs.find((tab) => tab.route === route);
            if (!existingTab && previousTabs.length >= displayTabCount) {
                return previousTabs;
            }
            const label = nextTab.label || existingTab?.label || formatRouteLabel(route);
            const currentPath = nextTab.currentPath || existingTab?.currentPath || normalizedRoute;
            const updatedTabs = existingTab
                ? previousTabs.map((tab) => (tab.route === route ? { ...tab, label, currentPath } : tab))
                : [...previousTabs, { route, label, currentPath }];

            sessionStorage.setItem('mainOpenTabs', JSON.stringify(updatedTabs));
            return updatedTabs;
        });
    };

    useEffect(() => {
        const handleOpenMainTab = (event) => {
            if (!event.detail?.route) {
                return;
            }

            const normalizedRoute = normalizeRoute(event.detail.route);
            const targetRoute = getBaseRoute(normalizedRoute);

            setCachedTabOutlets((previousCache) => {
                if (!Object.prototype.hasOwnProperty.call(previousCache, targetRoute)) {
                    return previousCache;
                }
                const updatedCache = { ...previousCache };
                delete updatedCache[targetRoute];
                return updatedCache;
            });
            setCachedTabPaths((previousCache) => {
                if (!Object.prototype.hasOwnProperty.call(previousCache, targetRoute)) {
                    return previousCache;
                }
                const updatedCache = { ...previousCache };
                delete updatedCache[targetRoute];
                return updatedCache;
            });

            if (isMultiTabEnabled) {
                setTabRefreshKeys((previousKeys) => ({
                    ...previousKeys,
                    [targetRoute]: (previousKeys[targetRoute] || 0) + 1,
                }));
                upsertTab({
                    ...event.detail,
                    route: normalizedRoute,
                    currentPath: normalizedRoute,
                });
            } else {
                setSingleTabRefreshKey((previousKey) => previousKey + 1);
            }
        };

        window.addEventListener('openMainTab', handleOpenMainTab);
        return () => window.removeEventListener('openMainTab', handleOpenMainTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMultiTabEnabled, displayTabCount]);

    useEffect(() => {
        if (isMultiTabEnabled && location.pathname !== '/login' && location.pathname !== '/') {
            upsertTab({
                route: getBaseRoute(location.pathname),
                currentPath: `${location.pathname}${location.search}`,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, location.search, isMultiTabEnabled, displayTabCount]);

    const activeTabRoute = openTabs
        .slice()
        .sort((first, second) => second.route.length - first.route.length)
        .find((tab) => location.pathname === tab.route || location.pathname.startsWith(`${tab.route}/`))
        ?.route;

    const handleTabChange = (route) => {
        if (route && route !== location.pathname) {
            const tab = openTabs.find((openTab) => openTab.route === route);

            navigate(tab?.currentPath || route, getModuleBasePath(route) ? {
                state: cachedTabOutlets[route]?.locationContext?.location?.state,
            } : undefined);
        }
    };

    const handleTabClose = (route) => {
        setCachedTabOutlets((previousCache) => {
            const updatedCache = { ...previousCache };
            delete updatedCache[route];
            return updatedCache;
        });
        setCachedTabPaths((previousCache) => {
            const updatedCache = { ...previousCache };
            delete updatedCache[route];
            return updatedCache;
        });
        setOpenTabs((previousTabs) => {
            const tabIndex = previousTabs.findIndex((tab) => tab.route === route);
            const updatedTabs = previousTabs.filter((tab) => tab.route !== route);
            sessionStorage.setItem('mainOpenTabs', JSON.stringify(updatedTabs));

            if (!updatedTabs.length) {
                navigate(EMPTY_TABS_ROUTE, { replace: true });
            } else if (route === activeTabRoute) {
                const nextTab = updatedTabs[tabIndex] || updatedTabs[tabIndex - 1];
                navigate(nextTab.currentPath || nextTab.route, getModuleBasePath(nextTab.route) ? {
                    state: cachedTabOutlets[nextTab.route]?.locationContext?.location?.state,
                } : undefined);
            }

            return updatedTabs;
        });
    };

    useEffect(() => {
        const currentPath = `${location.pathname}${location.search}`;

        if (!activeTabRoute || !outlet || cachedTabPaths[activeTabRoute] === currentPath) {
            return;
        }

        // Keep every open tab mounted until it is closed.
        setCachedTabOutlets((previousCache) => ({
            ...previousCache,
            [activeTabRoute]: {
                outlet,
                routeContext,
                locationContext,
            },
        }));
        setCachedTabPaths((previousCache) => ({
            ...previousCache,
            [activeTabRoute]: currentPath,
        }));
    }, [activeTabRoute, cachedTabOutlets, cachedTabPaths, location.pathname, location.search, locationContext, outlet, routeContext]);


    return (
        <>
            <Header openNav={open} onOpenNav={handleOpen} navWidth={navWidth} />
            <Helmet>
                <title> {headerName} | ProClaimz </title>
            </Helmet>
            <style>
                {
                    `
           .css-tte4fa-MuiFormHelperText-root{ color : red;}
           .css-icr2t7-MuiFormLabel-root-MuiInputLabel-root.Mui-error {
               color: unset !important;
           }
           .css-1eaco3s-MuiInputBase-root-MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline{
               border-color: unset !important;
           }
         `
                }
            </style>
            <style>
                {
                    `.css-1s95gw4-MuiButtonBase-root-MuiIconButton-root {padding:0px !important;}
                    `
                }
            </style>
            <Box
                sx={{
                    display: { lg: 'flex' },
                    minHeight: { lg: 1 },
                    height: '100%',
                    flex: 1,

                }}
                style={{ heigth: '100%', flex: 1 }}
            >

                <NavVertical openNav={open} onOpenNav={handleOpen} onCloseNav={handleClose} navWidth={navWidth} />
                <Main navWidth={navWidth}>
                    {isMultiTabEnabled ? (
                        <Box sx={{ height: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            <MainTabs
                                tabs={openTabs}
                                activeRoute={activeTabRoute}
                                onChange={handleTabChange}
                                onClose={handleTabClose}
                            />
                            <Box sx={{ flex: 1, minHeight: 0 }}>
                                {openTabs.map((tab) => {
                                    const cachedTab = cachedTabOutlets[tab.route];
                                    const isActiveTab = tab.route === activeTabRoute;
                                    const tabOutlet = cachedTab || (isActiveTab ? {
                                        outlet,
                                        routeContext,
                                        locationContext,
                                    } : null);

                                    return (
                                        <AppStoreProvider key={`${tab.route}-${tabRefreshKeys[tab.route] || 0}`}>
                                            <ModuleNavigationProvider active={isActiveTab} navigate={navigate}>
                                                <Box
                                                    sx={{
                                                        height: 1,
                                                        display: isActiveTab ? 'block' : 'none',
                                                    }}
                                                >
                                                    {tabOutlet && (
                                                        <FrozenRouterContext
                                                            locationContext={tabOutlet.locationContext}
                                                            routeContext={tabOutlet.routeContext}
                                                        >
                                                            {tabOutlet.outlet}
                                                        </FrozenRouterContext>
                                                    )}
                                                </Box>
                                            </ModuleNavigationProvider>
                                        </AppStoreProvider>
                                    );
                                })}
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ height: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            <AppStoreProvider key={singleTabRefreshKey}>
                                {outlet}
                            </AppStoreProvider>
                        </Box>
                    )}
                </Main>

            </Box>
            <Footer />
        </>
    );
}
